import { Injectable, OnDestroy } from '@angular/core';

import * as day from 'dayjs';
import { FlexTimeActivityInstance, FlexTimePeriod, IMingaFeatureToggleKeys } from 'libs/domain';
import { BehaviorSubject, lastValueFrom, ReplaySubject } from 'rxjs';

import { AuthInfoService } from '@app/src/app/minimal/services/AuthInfo';
import { FtmPeriodSelectorModalComponent } from '@app/src/app/modules/flex-time-manager/components/ftm-shared/components/ftm-period-selector-modal/ftm-period-selector-modal';
import { PeriodSelectorModalData } from '@app/src/app/modules/flex-time-manager/components/ftm-shared/ftm-shared.constants';
import { ModalOverlayService } from '@app/src/app/shared/components/modal-overlay';
import { SystemAlertSnackBarService } from '@app/src/app/shared/components/system-alert-snackbar';
import { CacheService } from '@app/src/app/shared/services/cache/cache.service';
import { CacheKey } from '@app/src/app/shared/services/cache/cache.types';
import { createCacheCollection } from '@app/src/app/shared/services/cache/cache.utils';
import { CheckinService } from '@app/src/app/shared/services/checkin';
import {
  FlexTimeActivityInstanceService,
  FlexTimePeriodService,
  FlexTimePermissionsService,
  FlexTimeRegistrationService,
  RegisteredPerson,
} from '@app/src/app/shared/services/flex-time';
import { FlexTimeRegistrationValidationService } from '@app/src/app/shared/services/flex-time/flex-time-registration-validation.service';
import { MingaSettingsService } from '@app/src/app/store/Minga/services';

import {
  AssignmentContext,
  AssignmentHandler,
  AssignmentResponse,
  AssignmentType,
  CheckInFormData,
  FlexAssignmentType,
  FormData,
  Student,
} from '../types/tt-my-class.types';

@Injectable()
export class MyClassFlexTimeService implements OnDestroy {
  private _destroyedSubject = new ReplaySubject<void>(1);

  private _revalidateUpcomingActivities = new BehaviorSubject<boolean>(false);
  public revalidateUpcomingActivities$ = this._revalidateUpcomingActivities.asObservable();

  private _todaysFlexPeriodsSubject = new BehaviorSubject<FlexTimePeriod[]>([]);
  public todaysFlexPeriods$ = this._todaysFlexPeriodsSubject.asObservable();

  private _upcomingPeriodSubject = new BehaviorSubject<FlexTimePeriod | null>(null);
  public upcomingPeriod$ = this._upcomingPeriodSubject.asObservable();

  private _assignHandlers: Record<FlexAssignmentType, AssignmentHandler> = {
    [FlexAssignmentType.REGISTER]: async (data, context) => {
      const teacher = !this._flexPermissions.isFlexTimeAdmin() ? this._auth.authPerson : null;
      const activityInstance = await this._flexInstanceService.openActivitySelector({
        selectorType: 'register',
        startDate: day(),
        endDate: day(),
        showDatePicker: true,
        teacher,
      });

      if (!activityInstance) {
        return {
          successHashes: [],
        };
      }

      const canOverride = this._flexPermissions.canManageActivityRegistration(activityInstance);

      await this._ftRegistration.register({
        activityId: activityInstance.id,
        periodId: activityInstance.flexTimePeriodId,
        selectedPeople: data.selectedStudents.map(s => ({
          ...s,
          displayName: `${s.firstName} ${s.lastName}`,
        })),
        canOverride,
      });

      // if we're registering for upcoming period we need to revalidate the upcoming activities of the current list of students
      if (this._isNextPeriod(activityInstance.flexTimePeriodId)) {
        this.revalidateUpcomingActivities();
      }

      return {
        // TODO not completely accurate if we hit an assign error but we're not currently using this for anything
        successHashes: data.selectedStudents.map(s => s.personHash),
      };
    },
    [FlexAssignmentType.ASSIGN]: async (data, context) => {
      const teacher = !this._flexPermissions.isFlexTimeAdmin() ? this._auth.authPerson : null;
      const activityInstance = await this._flexInstanceService.openActivitySelector({
        selectorType: 'assign',
        startDate: day(),
        endDate: day(),
        teacher,
        showDatePicker: true,
      });

      if (!activityInstance) {
        return {
          successHashes: [],
        };
      }
      const canOverride = this._flexPermissions.canManageActivityRegistration(activityInstance);

      await this._ftRegistration.assign({
        activityId: activityInstance.id,
        periodId: activityInstance.flexTimePeriodId,
        selectedPeople: data.selectedStudents.map(s => ({
          ...s,
          displayName: `${s.firstName} ${s.lastName}`,
        })),
        canOverride,
      });

      // if we're assign for upcoming period we need to revalidate the upcoming activities of the current list of students
      if (this._isNextPeriod(activityInstance.flexTimePeriodId)) {
        this.revalidateUpcomingActivities();
      }

      return {
        // TODO not completely accurate if we hit an assign error but we're not currently using this for anything
        successHashes: data.selectedStudents.map(s => s.personHash),
      };
    },
    [FlexAssignmentType.REGISTER_HOMEROOM]: async (data, context) => {
      const modalRef = this._modalService.open<PeriodSelectorModalData>(FtmPeriodSelectorModalComponent, {
        data: {
          modalTitle: "Register to Homeroom Teacher's Activity",
          message: "The students will be registered to their homeroom teacher's activity for the selected period",
        },
      });

      const response = await lastValueFrom(modalRef.afterClosed);

      if (!response.data?.period) {
        return {
          successHashes: [],
        };
      }

      const hashes = data.selectedStudents.map(s => s.personHash);
      const { successHashes } = await this._ftRegistration.registerToHomeroom({
        periodId: response.data.period.id,
        hashes,
      });

      return {
        successHashes,
      };
    },
    [FlexAssignmentType.CHECKIN]: async (data, context) => {
      const students = data.selectedStudents.map(s => ({
        ...s,
        displayName: `${s.firstName} ${s.lastName}`,
      }));
      const activityInstance = data.listFilter?.[0].data as FlexTimeActivityInstance;
      const isTardy = context?.isTardyCheckIn ?? false;
      const subGroup = data[AssignmentType.CHECK_IN] as CheckInFormData;
      const note = subGroup.note;
      const isFlexManagerOrHigher = this._flexPermissions.isFlexTimeSelfManagerOrHigher();

      if (!activityInstance) {
        throw new Error('No activity selected for check in');
      }

      const showProfilePicture = activityInstance.flexTimeActivity.showStudentPhotoOnCheckin;
      const activityName = activityInstance.flexTimeActivity.name;

      const { successHashes } = await this._checkinService.createCheckinWithDialogs(
        students,
        {
          id: activityInstance.checkinReasonId,
          name: activityName,
        },
        {
          note,
          meta: {
            isTardy,
          },
          showProfilePicture,
          canBypassError: isFlexManagerOrHigher,
        },
      );

      return {
        successHashes,
      };
    },
  };

  private _upcomingPeriodRegistrationsCache = createCacheCollection(key =>
    this._cacheService.create<RegisteredPerson[]>(
      `${CacheKey.MY_CLASS_PERIOD_REGISTRATIONS}:${key}`,
      async data => {
        return this._registrationService.fetchRegisteredPeople(undefined, {
          periodId: data.periodId,
        });
      },
      {
        ttl: 15,
      },
    ),
  );

  constructor(
    private _flexPeriodService: FlexTimePeriodService,
    private _settingService: MingaSettingsService,
    private _registrationService: FlexTimeRegistrationService,
    private _flexPermissions: FlexTimePermissionsService,
    private _flexInstanceService: FlexTimeActivityInstanceService,
    private _ftRegistration: FlexTimeRegistrationValidationService,
    private _modalService: ModalOverlayService,
    private _auth: AuthInfoService,
    private _systemSnackBar: SystemAlertSnackBarService,
    private _cacheService: CacheService,
    private _checkinService: CheckinService,
  ) {}

  ngOnDestroy(): void {
    this._destroyedSubject.next();
    this._destroyedSubject.complete();
    this._revalidateUpcomingActivities.complete();
  }

  public async assign(formData: FormData, context?: AssignmentContext): Promise<AssignmentResponse> {
    const type = formData.selectedAction.value as FlexAssignmentType;

    if (!this._assignHandlers[type]) {
      throw new Error(`Unknown flex assignment type: ${type}`);
    }

    return await this._assignHandlers[type](formData, context);
  }

  public async fetchTodaysFlexPeriods(): Promise<void> {
    try {
      const isFlexEnabled = await this._settingService.getModuleEnabled(IMingaFeatureToggleKeys.FLEX_TIME_ENABLED);

      if (!isFlexEnabled) return;

      const today = day();
      const periods = await this._flexPeriodService.fetchAll(today, today);

      this._todaysFlexPeriodsSubject.next(periods);
    } catch (error) {
      this._systemSnackBar.error("Failed to fetch today's flex periods");
    }
  }

  /**
   * Revalidates the upcoming activities for current student list
   */
  public revalidateUpcomingActivities(): void {
    this._revalidateUpcomingActivities.next(true);
  }

  public async getUpcomingActivityByStudent(
    periods: FlexTimePeriod[],
    students: Student[],
    opts?: {
      revalidate?: boolean;
    },
  ): Promise<Map<string, FlexTimeActivityInstance | undefined>> {
    const response = new Map();
    if (!students?.length || !periods?.length) {
      return response;
    }

    const today = day();
    const nextPeriod = this._flexPeriodService.getUpcomingPeriod(today, periods);
    if (!nextPeriod || !nextPeriod.activityInstances?.length) {
      return response;
    }
    this._upcomingPeriodSubject.next(nextPeriod);

    const cacheItem = this._upcomingPeriodRegistrationsCache.get(nextPeriod.id);
    const registered = await cacheItem
      .get(
        {
          periodId: nextPeriod.id,
        },
        opts,
      )
      .toPromise();

    if (!registered?.length) {
      return response;
    }

    const hashes = students.map(student => student.personHash);
    const activityInstances = nextPeriod.activityInstances;

    for (const hash of hashes) {
      const registration = registered.find(reg => reg.personHash === hash);

      if (!registration) continue;

      const activityInstance = activityInstances.find(i => i.id === registration.instanceId);
      response.set(hash, activityInstance);
    }

    return response;
  }

  private _isNextPeriod(periodId: number): boolean {
    const periods = this._todaysFlexPeriodsSubject.value;
    const today = day();
    const nextPeriod = this._flexPeriodService.getUpcomingPeriod(today, periods);
    return nextPeriod?.id === periodId;
  }
}